import { assertEvent, assign, raise, sendParent, setup } from 'xstate';

import type { PublishRulePackageInput, ViewRulePackagesQueryQuery } from '@/lib/gql/__generated__/graphql';
import { publishRulePackageActor, viewRulePackagesActor } from './rule-package.actors';
import type { RulePackageEvent } from './events';

type RulePackageContext = {
  items: ViewRulePackagesQueryQuery['viewRulePackages'];
  error: string | null;
};

type RulePackageMachineEvent = RulePackageEvent | { type: 'rule-package.changed' };

const messageFromError = (error: unknown): string =>
  error instanceof Error ? error.message : 'The Rule Package could not be loaded.';

export const rulePackageMachine = setup({
  types: {
    context: {} as RulePackageContext,
    events: {} as RulePackageMachineEvent,
  },
  actors: {
    viewRulePackagesActor,
    publishRulePackageActor,
  },
}).createMachine({
  id: 'rule-package',
  type: 'parallel',
  context: {
    items: [],
    error: null,
  },
  states: {
    viewRulePackages: {
      initial: 'idle',
      states: {
        idle: {},
        loading: {
          invoke: {
            id: 'viewRulePackages',
            src: 'viewRulePackagesActor',
            onDone: {
              target: 'ready',
              actions: assign({
                items: ({ event }) => event.output.viewRulePackages,
                error: () => null,
              }),
            },
            onError: {
              target: 'error',
              actions: assign({
                error: ({ event }) => messageFromError(event.error),
              }),
            },
          },
        },
        ready: {
          on: {
            'rule-package.load-viewRulePackages': 'loading',
            'rule-package.changed': 'loading',
          },
        },
        error: {
          on: {
            'rule-package.load-viewRulePackages': 'loading',
            'rule-package.changed': 'loading',
          },
        },
      },
      on: {
        'rule-package.load-viewRulePackages': '.loading',
        'rule-package.changed': '.loading',
      },
    },
    write: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            'rule-package.open-dialog': {
              actions: sendParent({
                type: 'ui.overlay.open',
                name: 'publishRulePackageDialog',
              }),
            },
            'rule-package.publish': 'saving',
          },
        },
        saving: {
          invoke: {
            id: 'publishRulePackage',
            src: 'publishRulePackageActor',
            input: ({ event }) => {
              assertEvent(event, 'rule-package.publish');
              const input: PublishRulePackageInput = {
                rulePackageId: crypto.randomUUID(),
                taxYear: event.input.taxYear,
                version: event.input.version,
                fileRef: event.input.fileRef,
              };
              return input;
            },
            onDone: {
              target: 'idle',
              actions: [
                assign({ error: () => null }),
                raise({ type: 'rule-package.changed' }),
                sendParent({ type: 'rule-package.published' }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: {
                    title: 'Rule Package published.',
                    tone: 'success',
                  },
                }),
                sendParent({
                  type: 'ui.overlay.close',
                  name: 'publishRulePackageDialog',
                }),
              ],
            },
            onError: {
              target: 'idle',
              actions: [
                assign({
                  error: ({ event }) => messageFromError(event.error),
                }),
                sendParent({
                  type: 'ui.toast.push',
                  toast: {
                    title: 'Rule Package could not be published.',
                    description: 'Check the form and try again.',
                    tone: 'error',
                  },
                }),
              ],
            },
          },
        },
      },
    },
  },
});
